import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Globe } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

export default function Contact() {
  const { t } = useLang();
  const { toast } = useToast();
  const [sendBusy, setSendBusy] = useState(false);
  const [msg, setMsg] = useState({ name: '', email: '', subject: '', message: '' });

  const send = async (e) => {
    e.preventDefault();
    if (!msg.email || !msg.message) {
      toast({ title: t('contact.missingTitle'), description: t('contact.missingDesc') });
      return;
    }
    setSendBusy(true);
    try {
      await api.post('/contacts', msg);
      toast({ title: t('contact.sentTitle'), description: t('contact.sentDesc') });
      setMsg({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: t('contact.errorTitle'), description: err?.response?.data?.detail || t('contact.errorDesc') });
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-brand-navy relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-yellow/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-brand-purple/20 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="text-white">
            <span className="inline-block text-brand-yellow text-xs font-semibold tracking-[0.35em] uppercase mb-4">{t('contact.label')}</span>
            <h2 className="text-4xl md:text-5xl font-bold leading-tight">
              {t('contact.title1')} <span className="text-brand-yellow">{t('contact.title2')}</span>
            </h2>
            <p className="text-white/80 mt-6 leading-relaxed max-w-lg">{t('contact.intro')}</p>

            <div className="space-y-5 mt-10">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-brand-yellow/15 border border-brand-yellow/30 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-brand-yellow" />
                </div>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">{t('contact.lEmail')}</div>
                  <div className="text-white font-medium">contact@yuville.com</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-brand-yellow/15 border border-brand-yellow/30 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-brand-yellow" />
                </div>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">{t('contact.lPhone')}</div>
                  <div className="text-white font-medium">+1 (514) 600 4416</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-brand-yellow/15 border border-brand-yellow/30 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-brand-yellow" />
                </div>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">{t('contact.lOfficeCA')}</div>
                  <div className="text-white font-medium">{t('contact.officeCAValue')}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-brand-yellow/15 border border-brand-yellow/30 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-brand-yellow" />
                </div>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">{t('contact.lOfficeCN')}</div>
                  <div className="text-white font-medium">{t('contact.officeCNValue')}</div>
                  <a href="https://qianyanholding.com" target="_blank" rel="noopener noreferrer" className="text-brand-yellow text-xs hover:underline">{t('contact.viaQianyan')}</a>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={send} className="bg-white rounded-2xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-brand-navy mb-6">{t('contact.formTitle')}</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input value={msg.name} onChange={(e) => setMsg({ ...msg, name: e.target.value })} placeholder={t('contact.name')}
                  className="px-4 py-3 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 transition" />
                <input value={msg.email} onChange={(e) => setMsg({ ...msg, email: e.target.value })} placeholder={t('contact.email')} type="email"
                  className="px-4 py-3 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 transition" />
              </div>
              <input value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })} placeholder={t('contact.subject')}
                className="w-full px-4 py-3 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 transition" />
              <textarea rows={6} value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })} placeholder={t('contact.message')}
                className="w-full px-4 py-3 border border-gray-200 rounded focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 transition resize-none" />
              <button type="submit" disabled={sendBusy} className="btn-yellow w-full justify-center disabled:opacity-60">
                {sendBusy ? t('contact.sending') : t('contact.send')} <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
